// Role-based product tours powered by driver.js
// Each step targets a data-testid selector already present in the UI.
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

// Common look & feel — matches the Just Compare navy/white brand
const commonOpts = {
  showProgress: true,
  animate: true,
  smoothScroll: true,
  allowClose: true,
  overlayColor: "rgba(15, 23, 42, 0.65)",
  stagePadding: 6,
  popoverClass: "jc-tour-popover",
  nextBtnText: "Next →",
  prevBtnText: "← Back",
  doneBtnText: "Got it",
};

// -------- Tour definitions per role --------

const ownerTour = [
  {
    element: '[data-testid="nav-dashboard"]',
    popover: {
      title: "Your command center",
      description: "The Dashboard summarizes projects, inventory, and any items running low across the warehouse.",
    },
  },
  {
    element: '[data-testid="nav-pending"]',
    popover: {
      title: "Pending Projects",
      description: "When sales reps submit a client for approval, it lands here. The amber badge tells you how many are waiting.",
    },
  },
  {
    element: '[data-testid="nav-inventory"]',
    popover: {
      title: "Warehouse Inventory",
      description: "Track every SKU across Office Furniture & Supply and Janitorial. Low-stock items surface automatically.",
    },
  },
  {
    element: '[data-testid="nav-vendors"]',
    popover: {
      title: "Vendors & Purchase Orders",
      description: "Link items to vendors, draft POs, mark them sent, and receive to auto-increment warehouse stock.",
    },
  },
  {
    element: '[data-testid="nav-sales"]',
    popover: {
      title: "The Sales pipeline",
      description: "See every rep's clients, priorities, follow-ups, and the map of nearby leads.",
    },
  },
  {
    element: '[data-testid="nav-calendar"]',
    popover: {
      title: "Calendar",
      description: "Daily crew assignments live here — approve a pending project and it appears on the schedule.",
    },
  },
  {
    element: '[data-testid="nav-users"]',
    popover: {
      title: "Team & Time Clock",
      description: "Manage roles and see who's clocked in. Only Owner and Installation Manager have access.",
    },
  },
  {
    popover: {
      title: "You're all set",
      description: "Any time you want a refresher, click <strong>Take the tour</strong> in the bottom-left corner.",
    },
  },
];

const salesTour = [
  {
    element: '[data-testid="nav-sales"]',
    popover: {
      title: "This is your workspace",
      description: "Every client you manage lives here — leads, follow-ups, priorities, and the map view.",
    },
  },
  {
    element: '[data-testid="sales-stats"]',
    popover: {
      title: "Your daily snapshot",
      description: "Tap any tile to filter — Leads, High Priority, or today's Follow-ups Due.",
    },
  },
  {
    element: '[data-testid="new-client-btn"]',
    popover: {
      title: "Add a client",
      description: "Capture company info, contact, address, priority, and a long-form memo. Addresses geocode automatically for the Nearby map.",
    },
  },
  {
    element: '[data-testid="tab-nearby"]',
    popover: {
      title: "Clients near you",
      description: "Switch to Nearby and hit <strong>Near me</strong> — the map filters clients within your chosen radius. Great for planning a route.",
    },
  },
  {
    element: '[data-testid="tab-phonebook"]',
    popover: {
      title: "Phone Book",
      description: "Every client's phone and email in one tap-to-call list.",
    },
  },
  {
    element: '[data-testid="nav-pending"]',
    popover: {
      title: "Submit → Approve flow",
      description: "Inside a client profile, click <strong>Submit for Approval</strong>. It lands here for the Installation Manager to approve. You'll see the status on the client card.",
    },
  },
  {
    popover: {
      title: "You're ready to sell",
      description: "Every morning at 7am we'll email you a digest of today's follow-ups. Click <strong>Take the tour</strong> anytime to replay this.",
    },
  },
];

const warehouseTour = [
  {
    element: '[data-testid="nav-inventory"]',
    popover: {
      title: "Warehouse Inventory",
      description: "Every SKU with its warehouse quantity. Watch for the red LOW badges on janitorial items.",
    },
  },
  {
    element: '[data-testid="scan-barcode-btn"]',
    popover: {
      title: "Scan a box barcode",
      description: "Point the phone camera at a box label (or type the SKU). We'll auto-open the Receive dialog with that item selected.",
    },
  },
  {
    element: '[data-testid="open-receive-btn"]',
    popover: {
      title: "Receive shipments",
      description: "Log every truck that rolls in — quantities flow into the warehouse total.",
    },
  },
  {
    element: '[data-testid="nav-purchase-orders"]',
    popover: {
      title: "Incoming purchase orders",
      description: "See what's expected, and when it arrives click <strong>Receive</strong> to auto-add units to inventory.",
    },
  },
  {
    element: '[data-testid="nav-deliveries"]',
    popover: {
      title: "Site deliveries",
      description: "Pre-load what needs to leave the warehouse for each install — the crew sees it on their calendar.",
    },
  },
  {
    element: '[data-testid="nav-time"]',
    popover: {
      title: "Clock in / out",
      description: "Punch the clock at the start and end of your shift.",
    },
  },
];

const TOURS = {
  owner: ownerTour,
  installation_manager: ownerTour,
  assistant_installation_manager: ownerTour,
  sales_representative: salesTour,
  warehouse_staff: warehouseTour,
  receptionist: salesTour, // reasonable default
};

export function tourStorageKey(userId) {
  return `jc:tour_seen:${userId || "anon"}`;
}

export function hasSeenTour(userId) {
  try { return localStorage.getItem(tourStorageKey(userId)) === "1"; }
  catch { return true; }
}

export function markTourSeen(userId) {
  try { localStorage.setItem(tourStorageKey(userId), "1"); } catch (_) {}
}

export function launchTour({ role, userId, onFinish }) {
  const steps = TOURS[role] || TOURS.sales_representative;
  // Filter out steps whose target element isn't on the page yet — driver.js
  // will still schedule them, but silently skip anything that can't be found.
  const d = driver({
    ...commonOpts,
    steps,
    onDestroyed: () => {
      markTourSeen(userId);
      onFinish?.();
    },
  });
  d.drive();
  return d;
}
